/**
 * PortalAppointments - Self-scheduling for client appointments
 * Deployed to: frontend/src/pages/portal/PortalAppointments.js
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Clock, CheckCircle, XCircle, ChevronLeft,
  ChevronRight, AlertCircle, RefreshCw
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalAppointments = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // list, book
  const [appointments, setAppointments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [config, setConfig] = useState(null);
  const [firmTz, setFirmTz] = useState('America/New_York');
  const [noAvailabilityMsg, setNoAvailabilityMsg] = useState('');
  const [attorneyId, setAttorneyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [meetingType, setMeetingType] = useState('in_person');
  const [meetingPurpose, setMeetingPurpose] = useState('');
  const [urgency, setUrgency] = useState('routine');
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    loadAppointments();
    clientPortalService.getAvailabilitySummary()
      .then(data => setAvailability(data)).catch(() => {});
  }, []);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const data = await clientPortalService.getAppointments();
      setAppointments(data.appointments || []);
    } catch (err) {
      console.error('Failed to load appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSlots = async () => {
    setLoading(true);
    try {
      const from = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
      const to = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0);
      const data = await clientPortalService.getAvailableSlots(
        attorneyId,
        from.toISOString().split('T')[0],
        to.toISOString().split('T')[0]
      );
      setSlots(data.slots || []);
      setConfig(data.config || null);
      if (data.timezone) setFirmTz(data.timezone);
      setNoAvailabilityMsg((data.slots && data.slots.length) ? '' : (data.message || ''));
      if (data.attorney_id) setAttorneyId(data.attorney_id);
    } catch (err) {
      console.error('Failed to load slots:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'book') loadSlots();
  }, [view, calendarDate]);

  // Snap the meeting type into the selected slot's allowed list — a slot that
  // only offers phone must never submit the in_person initial state.
  useEffect(() => {
    const allowed = selectedSlot?.allowed_meeting_types;
    if (allowed && allowed.length && !allowed.includes(meetingType)) {
      setMeetingType(allowed[0]);
    }
  }, [selectedSlot]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBook = async () => {
    if (!selectedSlot) return;
    setBooking(true); setError('');
    try {
      await clientPortalService.bookAppointment({
        attorney_id: attorneyId,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: bookingNotes,
        meeting_type: meetingType,
        meeting_purpose: meetingPurpose,
        urgency: urgency,
      });
      setView('list');
      setSelectedSlot(null);
      setBookingNotes('');
      setMeetingType('in_person');
      setMeetingPurpose('');
      setUrgency('routine');
      loadAppointments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setBooking(false);
    }
  };

  const handleCancel = async (id) => {
    const reason = window.prompt('Reason for cancellation (optional):');
    try {
      await clientPortalService.cancelAppointment(id, reason || '');
      loadAppointments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Cannot cancel appointment');
    }
  };

  // Group slots by date
  const slotsByDate = {};
  slots.forEach(s => {
    const d = s.date;
    if (!slotsByDate[d]) slotsByDate[d] = [];
    slotsByDate[d].push(s);
  });

  const datesWithSlots = Object.keys(slotsByDate);

  // Calendar navigation
  const prevMonth = () => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  const nextMonth = () => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));

  // Build calendar grid
  const buildCalendarDays = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  // Times are UTC instants; render them in the FIRM timezone (label with the
  // firm's abbreviation, e.g. EDT). A tz-less ISO string is treated as UTC.
  const toUtc = (isoStr) => {
    if (!isoStr) return null;
    const hasTz = /[Zz]$|[+-]\d\d:?\d\d$/.test(isoStr);
    return new Date(hasTz ? isoStr : isoStr + 'Z');
  };
  const formatTime = (isoStr) => {
    const d = toUtc(isoStr);
    return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: firmTz }) : '';
  };
  const formatDateFirm = (isoStr, opts) => {
    const d = toUtc(isoStr);
    return d ? d.toLocaleDateString(undefined, { ...opts, timeZone: firmTz }) : '';
  };
  const tzAbbr = (isoStr) => {
    const d = toUtc(isoStr) || new Date();
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: firmTz, timeZoneName: 'short' }).formatToParts(d);
    return (parts.find(p => p.type === 'timeZoneName') || {}).value || '';
  };

  const statusColors = {
    confirmed: 'bg-green-500/20 text-green-300 border-green-500/30',
    pending_approval: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    rejected: 'bg-red-500/20 text-red-300 border-red-500/30',
    cancelled: 'bg-red-500/20 text-red-300 border-red-500/30',
    completed: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  };

  const meetingTypeLabels = { in_person: 'In-Person', video: 'Video Call', phone: 'Phone Call' };
  const meetingTypeIcons = { in_person: '\u{1F3E2}', video: '\u{1F4F9}', phone: '\u{1F4DE}' };
  const urgencyLabels = { routine: 'Routine', time_sensitive: 'Time-Sensitive', urgent: 'Urgent' };
  const urgencyColors = { routine: 'text-virgo-teal-400', time_sensitive: 'text-amber-400', urgent: 'text-red-400' };

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => view === 'book' ? setView('list') : navigate('/portal/dashboard')} className="text-virgo-teal-400 hover:text-white"><ArrowLeft size={24} /></button>
            <Calendar className="text-cyan-400" size={24} />
            <h1 className="text-xl font-bold text-white">{view === 'book' ? 'Schedule Appointment' : 'My Appointments'}</h1>
          </div>
          {view === 'list' && (
            <button onClick={() => setView('book')} className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg text-sm font-semibold hover:from-cyan-600 hover:to-blue-600">
              Schedule New
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
          </div>
        ) : view === 'list' ? (
          /* Appointment List */
          <div className="space-y-4">
            {appointments.length === 0 ? (
              <div className="text-center py-16 text-virgo-teal-500">
                <Calendar className="mx-auto mb-3 opacity-50" size={48} />
                <p>No appointments scheduled</p>
                <button onClick={() => setView('book')} className="mt-3 px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-lg text-sm hover:bg-cyan-500/30">
                  Schedule your first appointment
                </button>
              </div>
            ) : appointments.map(appt => (
              <div key={appt.id} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-500/20 rounded-lg"><Clock className="text-cyan-400" size={18} /></div>
                    <div>
                      <p className="text-white font-semibold">
                        {formatDateFirm(appt.start_time, { weekday: 'long', month: 'long', day: 'numeric' })}
                      </p>
                      <p className="text-virgo-teal-400 text-sm">
                        {formatTime(appt.start_time)} - {formatTime(appt.end_time)} {tzAbbr(appt.start_time)}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs border ${statusColors[appt.status] || statusColors.confirmed}`}>
                    {appt.status === 'pending_approval' ? 'Pending Approval' : appt.status.charAt(0).toUpperCase() + appt.status.slice(1)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-virgo-teal-500">
                  <span>With: {appt.attorney_name}</span>
                  {appt.meeting_type && <span className="flex items-center gap-1">{meetingTypeIcons[appt.meeting_type]} {meetingTypeLabels[appt.meeting_type]}</span>}
                  {appt.urgency && appt.urgency !== 'routine' && <span className={urgencyColors[appt.urgency]}>{urgencyLabels[appt.urgency]}</span>}
                </div>
                {appt.meeting_purpose && <p className="text-virgo-teal-500 text-sm mt-1">Purpose: {appt.meeting_purpose}</p>}
                {appt.notes && <p className="text-virgo-teal-500 text-sm mt-1">Notes: {appt.notes}</p>}
                {appt.rejection_reason && <p className="text-red-400 text-sm mt-1">Reason: {appt.rejection_reason}</p>}
                {appt.status === 'pending_approval' && (
                  <div className="mt-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                    <p className="text-amber-300 text-xs">Awaiting attorney approval. You'll be notified once confirmed.</p>
                  </div>
                )}
                {(appt.status === 'confirmed' || appt.status === 'pending_approval') && toUtc(appt.start_time) > new Date() && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => handleCancel(appt.id)} className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-300 rounded text-sm hover:bg-red-500/30">
                      <XCircle size={14} /> Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* Booking View */
          <div>
          {noAvailabilityMsg && (
            <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center text-amber-200 text-sm">
              Online self-scheduling isn't available right now. Please contact the office to book an appointment.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Calendar */}
            <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-5">
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="text-virgo-teal-400 hover:text-white"><ChevronLeft size={20} /></button>
                <h3 className="text-white font-semibold">
                  {calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h3>
                <button onClick={nextMonth} className="text-virgo-teal-400 hover:text-white"><ChevronRight size={20} /></button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-xs text-virgo-teal-500 mb-2">
                {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d}>{d}</div>)}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {buildCalendarDays().map((day, i) => {
                  if (!day) return <div key={i} />;
                  const dateStr = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const hasSlots = datesWithSlots.includes(dateStr);
                  const isSelected = selectedDate === dateStr;

                  return (
                    <button key={i} onClick={() => hasSlots && setSelectedDate(dateStr)}
                      disabled={!hasSlots}
                      className={`p-2 rounded-lg text-sm transition-all ${
                        isSelected ? 'bg-cyan-500 text-white' :
                        hasSlots ? 'bg-virgo-navy-600 text-white hover:bg-cyan-500/30 cursor-pointer' :
                        'text-virgo-teal-600 cursor-default'
                      }`}>
                      {day}
                      {hasSlots && <div className="w-1 h-1 bg-cyan-400 rounded-full mx-auto mt-0.5" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Slots + Booking */}
            <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-5">
              {!selectedDate ? (
                <div className="text-center py-8 text-virgo-teal-500">
                  <Clock className="mx-auto mb-3 opacity-50" size={36} />
                  <p>Select a date to see available times</p>
                </div>
              ) : (
                <>
                  <h3 className="text-white font-semibold mb-3">
                    {new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h3>
                  <div className="grid grid-cols-3 gap-2 mb-4 max-h-48 overflow-y-auto">
                    {(slotsByDate[selectedDate] || []).map((slot, i) => (
                      <button key={i}
                        onClick={() => setSelectedSlot(slot)}
                        className={`px-3 py-2 rounded-lg text-sm transition-all ${
                          selectedSlot?.start_time === slot.start_time
                            ? 'bg-cyan-500 text-white'
                            : 'bg-virgo-navy-600 text-virgo-teal-300 hover:bg-cyan-500/30'
                        }`}>
                        {slot.local_time || formatTime(slot.start_time)}
                      </button>
                    ))}
                  </div>

                  {selectedSlot && (
                    <div className="border-t border-virgo-teal-700/30 pt-4 space-y-3">
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                        <p className="text-cyan-300 text-sm font-semibold">Selected Time</p>
                        <p className="text-white">{formatTime(selectedSlot.start_time)} - {formatTime(selectedSlot.end_time)} {tzAbbr(selectedSlot.start_time)}</p>
                        <p className="text-virgo-teal-500 text-xs">{selectedSlot.duration_minutes} minutes</p>
                      </div>

                      {/* Meeting Type */}
                      <div>
                        <label className="text-virgo-teal-400 text-xs font-semibold mb-1 block">Meeting Type</label>
                        <div className="grid grid-cols-3 gap-2">
                          {(selectedSlot.allowed_meeting_types || ['in_person', 'video', 'phone']).map(t => (
                            <button key={t} type="button" onClick={() => setMeetingType(t)}
                              className={`px-3 py-2 rounded-lg text-sm transition-all ${
                                meetingType === t ? 'bg-cyan-500 text-white' : 'bg-virgo-navy-600 text-virgo-teal-300 hover:bg-cyan-500/30'
                              }`}>
                              {meetingTypeLabels[t]}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Purpose */}
                      <div>
                        <label className="text-virgo-teal-400 text-xs font-semibold mb-1 block">What would you like to discuss?</label>
                        <textarea
                          value={meetingPurpose}
                          onChange={(e) => setMeetingPurpose(e.target.value)}
                          placeholder="Brief description of what you'd like to cover..."
                          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                          rows={2}
                        />
                      </div>

                      {/* Urgency */}
                      <div>
                        <label className="text-virgo-teal-400 text-xs font-semibold mb-1 block">Urgency</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[['routine', 'Routine'], ['time_sensitive', 'Time-Sensitive'], ['urgent', 'Urgent']].map(([val, label]) => (
                            <button key={val} type="button" onClick={() => setUrgency(val)}
                              className={`px-3 py-2 rounded-lg text-sm transition-all ${
                                urgency === val ? (val === 'urgent' ? 'bg-red-500 text-white' : val === 'time_sensitive' ? 'bg-amber-500 text-white' : 'bg-cyan-500 text-white')
                                : 'bg-virgo-navy-600 text-virgo-teal-300 hover:bg-cyan-500/30'
                              }`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <textarea
                        value={bookingNotes}
                        onChange={(e) => setBookingNotes(e.target.value)}
                        placeholder="Notes (optional)"
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                        rows={2}
                      />
                      {error && (
                        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-2 flex items-center gap-2">
                          <AlertCircle size={14} className="text-red-400" />
                          <span className="text-red-300 text-sm">{error}</span>
                        </div>
                      )}
                      <button onClick={handleBook} disabled={booking}
                        className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all">
                        {booking ? 'Booking...' : 'Confirm Appointment'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalAppointments;
